import type { Availability, Media, Product, ProductContent, Variant } from '../model/product';

type OptionsShape = Pick<ProductContent, 'colors' | 'sizes' | 'oneSizeLabel' | 'kind'>;

export type VariantSelection = { colorId: string | null; size: string | null };

/** What variant choice needs from a product: its colours, sizes and variants. */
export type VariantsShape = Pick<Product, 'colors' | 'sizes' | 'variants'>;

export function findVariant(product: VariantsShape, selection: VariantSelection): Variant | null {
  const needsColor = product.colors.length > 0;
  const needsSize = product.sizes.length > 0;
  return (
    product.variants.find(
      (variant) =>
        (!needsColor || variant.colorId === selection.colorId) &&
        (!needsSize || variant.size === selection.size),
    ) ?? null
  );
}

/** Availability of one size in one colour; a missing variant is out of stock. */
export function optionAvailability(
  product: VariantsShape,
  colorId: string | null,
  size: string | null,
): Availability {
  return findVariant(product, { colorId, size })?.availability ?? 'out_of_stock';
}

export function isColorAvailable(product: VariantsShape, colorId: string): boolean {
  return product.variants.some(
    (variant) => variant.colorId === colorId && variant.availability !== 'out_of_stock',
  );
}

export function defaultColorId(product: VariantsShape): string | null {
  return (
    product.colors.find((color) => isColorAvailable(product, color.id))?.id ??
    product.colors[0]?.id ??
    null
  );
}

/** The only purchasable variant of a product without size grid, for a colour. */
export function defaultSize(product: Pick<Product, 'sizes'>): string | null {
  return product.sizes.length === 1 ? (product.sizes[0] ?? null) : null;
}

export function colorName(
  product: Pick<ProductContent, 'colors'>,
  colorId: string | null,
): string | null {
  return product.colors.find((color) => color.id === colorId)?.name ?? null;
}

export function colorTint(
  product: Pick<ProductContent, 'colors'>,
  colorId: string | null,
): string | null {
  return product.colors.find((color) => color.id === colorId)?.tint ?? null;
}

/** "Графит · Размер M", "Шалфей", "Единый размер". */
export function variantLabel(
  product: OptionsShape,
  variant: Pick<Variant, 'colorId' | 'size'>,
): string {
  const color = colorName(product, variant.colorId);
  if (product.sizes.length > 1 && variant.size)
    return color ? `${color} · Размер ${variant.size}` : `Размер ${variant.size}`;
  return color ?? product.oneSizeLabel ?? 'Единый размер';
}

export function primaryMedia(product: Pick<ProductContent, 'media'>): Media | null {
  return (
    product.media.find((item) => item.kind === 'photo' || item.kind === 'sketch') ??
    product.media[0] ??
    null
  );
}

/** Badge shown on cards: preorder wins, then in stock. */
export function availabilityBadge(product: Product): Availability {
  if (product.variants.some((variant) => variant.availability === 'preorder')) return 'preorder';
  if (product.variants.some((variant) => variant.availability === 'in_stock')) return 'in_stock';
  return 'out_of_stock';
}

/** Compact colour/size summary for cards: "Молочный, Графит · XS–XL". */
export function optionsSummary(product: Product): string {
  const colors = product.colors
    .filter((color) => isColorAvailable(product, color.id))
    .map((color) => color.name);
  const sizes =
    product.sizes.length > 1
      ? `${product.sizes[0]}–${product.sizes[product.sizes.length - 1]}`
      : null;
  if (colors.length && sizes) return `${colors.join(', ')} · ${sizes}`;
  if (colors.length) return colors.join(', ');
  return product.kind === 'bundle' ? 'Набор' : (product.oneSizeLabel ?? 'Единый размер');
}
